import React, { createContext, useContext, useState } from "react";

interface ChatbotContextType {
  /** Whether the chatbot panel is currently open. */
  isOpen: boolean;
  /** True only inside a real provider — lets shared chrome (the header) decide whether to show the launcher. */
  enabled: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

export const ChatbotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const value: ChatbotContextType = {
    isOpen,
    enabled: true,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  };

  return <ChatbotContext.Provider value={value}>{children}</ChatbotContext.Provider>;
};

const noop = () => {};

/**
 * Returns the chatbot context. Unlike most context hooks here, this one returns a safe
 * disabled default instead of throwing when called outside a provider — CustomTopBar is a
 * shared component rendered for every role, but the chatbot launcher must only appear (and
 * the context only exists) for organization users. Consumers gate the launcher on `enabled`.
 */
export const useChatbot = (): ChatbotContextType => {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    return { isOpen: false, enabled: false, open: noop, close: noop, toggle: noop };
  }
  return context;
};
